import { DEFAULT_NOTE_STYLE, type NoteSection } from './classroom-note-share';

export interface StudyNoteSection {
  readonly heading: string | null;
  readonly items: readonly string[];
}
export interface StudyNote {
  readonly title: string;
  readonly sections: readonly StudyNoteSection[];
}

/** Short labels can share a row; prose always retains the full reading width. */
export function studyNoteUsesColumns(section: StudyNoteSection): boolean {
  return (
    section.items.length >= 2 &&
    section.items.length <= 3 &&
    section.items.every((item) => [...item].length <= 4 && !/\s/u.test(item))
  );
}

/** The last two short lines leave room for the footer drawing on a desktop sheet. */
export function studyNoteHasFooterSpace(note: StudyNote): boolean {
  const last = note.sections.at(-1);
  return (
    !!last &&
    !studyNoteUsesColumns(last) &&
    last.items.length >= 2 &&
    last.items.slice(-2).every((item) => [...item].length <= 11 && !/[\r\n]/u.test(item))
  );
}

function isNumberedSubitem(text: string): boolean {
  return /^(?:\(\s*\d+\s*\)|（\s*\d+\s*）|【\d+】)\s*\S/u.test(text);
}

function numberedHeading(text: string, followingText: string): string | null {
  const explicit = /^\d+[.．、]\s*(.{1,60}?)[：:]$/u.exec(text);
  if (explicit) return explicit[1]!.trim();

  // A short numbered label followed by parenthesized subitems is an outline heading,
  // even without a colon. Ordinary numbered prose/list items keep their original place.
  const outline = /^\d+[.．、]\s*([^，,。.!！?？;；:：\r\n]{1,40})$/u.exec(text);
  return outline && isNumberedSubitem(followingText) ? outline[1]!.trim() : null;
}

/** Structural editing only. Never infer a lesson, explanation, exercise or date. */
export function organizeClassroomNote(source: readonly NoteSection[]): StudyNote {
  const sections: StudyNoteSection[] = [];
  const paragraphs = source.flatMap((group, groupIndex) =>
    group
      .map((paragraph) => ({
        text: paragraph
          .map((run) => run.text)
          .join('')
          .trim(),
        groupIndex,
      }))
      .filter(({ text }) => text),
  );
  let heading: string | null = null;
  let items: string[] = [];
  const flush = () => {
    if (heading || items.length) sections.push({ heading, items });
    heading = null;
    items = [];
  };
  paragraphs.forEach(({ text, groupIndex }, index) => {
    if (
      index > 0 &&
      groupIndex !== paragraphs[index - 1]!.groupIndex &&
      !(heading && isNumberedSubitem(text))
    ) {
      flush();
    }
    const title = numberedHeading(text, paragraphs[index + 1]?.text ?? '');
    if (title) {
      flush();
      heading = title;
    } else {
      items.push(text.replace(/^【\d+】\s*/u, ''));
    }
  });
  flush();
  const headings = sections.flatMap((section) => (section.heading ? [section.heading] : []));
  const firstLine = sections[0]?.items[0] ?? '课堂笔记';
  const title = headings.length
    ? headings.slice(0, 4).join('、')
    : firstLine.length > 40
      ? `${firstLine.slice(0, 39)}…`
      : firstLine;
  return { title, sections };
}

/** Shared reading hierarchy for the live DOM preview and the high-resolution PNG. */
export function studyNoteExportSections(note: StudyNote): readonly NoteSection[] {
  const body = { ...DEFAULT_NOTE_STYLE, color: '#3E342C', fontSize: 17 };
  return note.sections.map((section, index) => [
    ...(section.heading
      ? [
          [
            {
              ...body,
              fontSize: 13,
              color: '#877657',
              text: `${String(index + 1).padStart(2, '0')}  `,
            },
            { ...body, fontSize: 19, bold: true, color: '#657957', text: section.heading },
          ],
        ]
      : []),
    ...section.items.map((text) => [{ ...body, text: `·  ${text}` }]),
  ]);
}
